
import { useState } from "react";
import { UserCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnalysisUploader } from "@/components/analysis";
import AnalysisResults, { ResultLevel } from "@/components/AnalysisResults";

const ProxyInterview = () => {
  const [results, setResults] = useState<{
    results: {
      type: string;
      level: ResultLevel;
      score: number;
      details: string;
    }[];
    overallStatus: ResultLevel;
  } | null>(null);

  const handleAnalyze = async (file: File | string) => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        const mockResults = {
          results: [
            {
              type: "Voice Consistency",
              level: "fraudulent" as ResultLevel,
              score: 92,
              details: "Multiple voice patterns detected, suggesting different speakers.",
            },
            {
              type: "Background Analysis",
              level: "suspicious" as ResultLevel,
              score: 63,
              details: "Some inconsistent background noise patterns detected.",
            },
            {
              type: "Response Timing",
              level: "fraudulent" as ResultLevel,
              score: 88,
              details: "Abnormal pauses and response timing indicates potential assistance.",
            },
            {
              type: "Technical Indicators",
              level: "suspicious" as ResultLevel,
              score: 68,
              details: "Multiple device signatures detected in the session data.",
            },
          ],
          overallStatus: "fraudulent" as ResultLevel,
        };
        
        setResults(mockResults);
        resolve();
      }, 2000);
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Proxy Interview Detection</h1>
        <p className="text-muted-foreground">
          Identify if someone is using assistance or being replaced during remote interviews
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <AnalysisUploader
            title="Proxy Interview Analysis"
            description="Upload a recording of the interview to analyze"
            acceptedFileTypes=".mp3,.mp4,.wav,.webm"
            icon={<UserCheck className="h-5 w-5 text-fraudeye-accent" />}
            onAnalyze={handleAnalyze}
            allowText={false}
          />

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
              <CardDescription>
                Our proxy interview detection uses multiple analysis techniques
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium">Voice Consistency Analysis</h3>
                <p className="text-sm text-muted-foreground">
                  Identifies subtle voice changes, multiple speakers, or voice modulation technology.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Background Environment</h3>
                <p className="text-sm text-muted-foreground">
                  Analyzes background noise consistency throughout the interview session.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Response Pattern Analysis</h3>
                <p className="text-sm text-muted-foreground">
                  Detects unnatural pauses, timing patterns, or signs of external assistance.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Technical Indicators</h3>
                <p className="text-sm text-muted-foreground">
                  Examines technical data for signs of multiple devices or unusual network patterns.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          {results ? (
            <AnalysisResults {...results} />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <UserCheck className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">No Analysis Results Yet</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md">
                    Upload an interview recording to begin analysis. Results will appear here once processing is complete.
                  </p>
                  <Alert variant="default" className="max-w-md border-fraudeye-accent/50 bg-fraudeye-accent/10">
                    <AlertTriangle className="h-4 w-4 text-fraudeye-accent" />
                    <AlertTitle>Tip</AlertTitle>
                    <AlertDescription>
                      Longer recordings provide more accurate results as patterns become more apparent.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Button className="w-full" variant="outline">
              Export Results
            </Button>
            <Button className="w-full bg-fraudeye-accent hover:bg-fraudeye-highlight">
              New Analysis
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProxyInterview;
